// src/utils/api-certificates.js
//
// Aprobación del jefe de laboratorio y emisión del certificado.
//
// El certificado NO se emite cuando el técnico calcula: se emite recién cuando
// el jefe aprueba. Al aprobar, acá se hace todo el circuito de una:
//   generar PDF -> subir a la carpeta del cliente -> registrar en `certificates`
//   -> cerrar la sesión -> registrar el hito "Revisado" sin hacer retroceder
//      un instrumento que ya esté remitido o entregado.
//
// Sirve a eléctrica y presión (viven en tablas distintas): la tabla
// `certificates` es el registro único que después alimenta el link corto /c/<nro>,
// el buscador y el portal del cliente.

import { supabase } from './supabase';
import { isClosedInstrumentStatus } from './instrument-workflow';
import { certificateObjectPath, sha256 } from './certificate-retention';
import { generateElectricalNoSacCertificatePdf } from './electrical-certificate-pdf';
import { generatePressureNoSacCertificatePdf } from './pressure-certificate-pdf';
import { generateTemperatureNoSacCertificatePdf } from './temperature-certificate-pdf';
import { generateFlowNoSacCertificatePdf } from './flow-certificate-pdf';
import { generateDimensionalNoSacCertificatePdf } from './dimensional-certificate-pdf';
import { generateEnvironmentalNoSacCertificatePdf } from './environmental-certificate-pdf';
import { generateAnalyticalNoSacCertificatePdf } from './analytical-certificate-pdf';
import { generateMassNoSacCertificatePdf } from './mass-certificate-pdf';
import { generateForceNoSacCertificatePdf } from './force-certificate-pdf';
import { generateGasNoSacCertificatePdf } from './gas-certificate-pdf';
import { generateSafetyValveNoSacCertificatePdf } from './safety-valve-certificate-pdf';
import { generateCheckValveNoSacCertificatePdf } from './check-valve-certificate-pdf';
import {
  getElectricalSession,
  listElectricalBlocks,
  listElectricalResults,
  updateElectricalSession,
  addValidityDaysToIsoDate,
  DEFAULT_VALIDITY_MONTHS as ELECTRICAL_MONTHS,
} from './api-electrical-calibration';
import {
  getPressureManometerSession,
  listPressureManometerResults,
  updatePressureManometerSession,
  addPressureValidityDaysToIsoDate,
  DEFAULT_VALIDITY_MONTHS as PRESSURE_MONTHS,
} from './api-pressure-manometer-pilot';
import {
  getTemperatureSession,
  listTemperaturePoints,
  listTemperatureResults,
  updateTemperatureSession,
  addTemperatureValidityDaysToIsoDate,
  DEFAULT_VALIDITY_MONTHS as TEMPERATURE_MONTHS,
} from './api-temperature-calibration';
import {
  getFlowSession,
  listFlowPoints,
  listFlowResults,
  updateFlowSession,
  addFlowValidityDaysToIsoDate,
  DEFAULT_FLOW_VALIDITY_MONTHS,
} from './api-flow-calibration';
import {
  getDimensionalSession,
  listDimensionalBlocks,
  listDimensionalPoints,
  listDimensionalResults,
  updateDimensionalSession,
  addDimensionalValidityDaysToIsoDate,
  DEFAULT_DIMENSIONAL_VALIDITY_MONTHS,
} from './api-dimensional-calibration';
import {
  getEnvironmentalSession,
  listEnvironmentalPoints,
  listEnvironmentalResults,
  updateEnvironmentalSession,
  addEnvironmentalValidityDaysToIsoDate,
  DEFAULT_ENVIRONMENTAL_VALIDITY_MONTHS,
} from './api-environmental-calibration';
import {
  getAnalyticalSession,
  listAnalyticalPoints,
  listAnalyticalResults,
  updateAnalyticalSession,
  addAnalyticalValidityDaysToIsoDate,
  DEFAULT_ANALYTICAL_VALIDITY_MONTHS,
} from './api-analytical-calibration';
import {
  getMassSession,
  listMassPoints,
  listMassResults,
  updateMassSession,
  addMassValidityDaysToIsoDate,
  DEFAULT_MASS_VALIDITY_MONTHS,
} from './api-mass-calibration';
import {
  getForceSession,
  listForceResults,
  updateForceSession,
  addForceValidityDaysToIsoDate,
  DEFAULT_FORCE_VALIDITY_MONTHS,
} from './api-force-calibration';
import {
  getGasSession,
  listGasResults,
  updateGasSession,
  addGasValidityDaysToIsoDate,
  DEFAULT_GAS_VALIDITY_MONTHS,
} from './api-gas-calibration';
import {
  getSafetyValveSession,
  listSafetyValveResults,
  updateSafetyValveSession,
  addValveValidityDaysToIsoDate,
  DEFAULT_VALVE_VALIDITY_MONTHS,
} from './api-safety-valve-calibration';
import {
  getCheckValveSession,
  listCheckValveResults,
  updateCheckValveSession,
} from './api-check-valve-calibration';

const BUCKET = 'certificates';

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}
function asIsoDate(v) {
  return v ? String(v).slice(0, 10) : null;
}

// "OS-6499-003" -> "6499003" · "6499003" -> "6499003"
export function resolveCertificateNumber(...values) {
  for (const v of values) {
    if (v === null || v === undefined) continue;
    const t = String(v).trim();
    if (/^\d+$/.test(t)) return t;
    const m = t.match(/OS-(\d+)-(\d+)$/i);
    if (m) return `${m[1]}${m[2]}`;
  }
  return null;
}

async function currentUserId() {
  return (await supabase.auth.getUser())?.data?.user?.id ?? null;
}

const INSTRUMENT_COLS =
  'id, lab_id, tag, serial, type, brand, model, client_id, client:clients(id, name)';

// ── Bandeja del jefe ────────────────────────────────────────────────────────
export async function listPendingApprovals() {
  const { data, error } = await supabase
    .from('v_pending_certificate_approvals')
    .select('*')
    .order('session_created_at', { ascending: false });
  if (error) throw error;

  const rows = data || [];
  const ids = [...new Set(rows.map((r) => r.instrument_id).filter(Boolean))];
  if (!ids.length) return rows;

  const { data: instr } = await supabase.from('instruments').select(INSTRUMENT_COLS).in('id', ids);
  const map = Object.fromEntries((instr || []).map((i) => [i.id, i]));
  return rows.map((r) => ({ ...r, instrument: map[r.instrument_id] || null }));
}

// ── Armado del PDF (según la magnitud) ──────────────────────────────────────
async function buildCertificate(row) {
  let instrument = row.instrument;
  if (!instrument) {
    const { data } = await supabase.from('instruments').select(INSTRUMENT_COLS).eq('id', row.instrument_id).single();
    instrument = data;
  }

  if (row.family === 'electrical') {
    const base = await getElectricalSession(row.session_id);
    const [blocks, results] = await Promise.all([
      listElectricalBlocks(row.session_id),
      listElectricalResults(row.session_id),
    ]);
    if (!results?.length) throw new Error('La sesión no tiene resultados calculados.');

    // Fechas definitivas: la EMISIÓN es hoy (cuando el jefe aprueba). Antes las
    // fijaba el botón del técnico; ahora se resuelven acá para que el PDF nunca
    // salga sin fechas.
    const months = Number(base.validity_months) || ELECTRICAL_MONTHS;
    const calibrationDate = asIsoDate(base.calibration_date) || asIsoDate(base.report_issue_date) || todayIso();
    const datePatch = {
      calibration_date: calibrationDate,
      report_issue_date: todayIso(),
      validity_months: months,
      valid_until: asIsoDate(base.valid_until) || addValidityDaysToIsoDate(calibrationDate, months),
    };
    const session = { ...base, ...datePatch };

    const serviceOrderCode = session.service_order_code || '';
    const number = resolveCertificateNumber(serviceOrderCode, session.certificate_number, instrument?.lab_id);
    const blob = await generateElectricalNoSacCertificatePdf(
      { session, instrument, blocks, results },
      { returnBlob: true, serviceOrderCode }
    );
    return {
      blob, number, session, instrument, serviceOrderCode, datePatch,
      calibrationDate, validUntil: datePatch.valid_until,
    };
  }

  if (row.family === 'temperature') {
    const base = await getTemperatureSession(row.session_id);
    const [points, results] = await Promise.all([
      listTemperaturePoints(row.session_id),
      listTemperatureResults(row.session_id),
    ]);
    if (!results?.length) throw new Error('La sesión no tiene resultados calculados.');

    const months = Number(base.validity_months) || TEMPERATURE_MONTHS;
    const calibrationDate = asIsoDate(base.calibration_date) || asIsoDate(base.report_issue_date) || todayIso();
    const datePatch = {
      calibration_date: calibrationDate,
      report_issue_date: todayIso(),
      validity_months: months,
      valid_until: asIsoDate(base.valid_until) || addTemperatureValidityDaysToIsoDate(calibrationDate, months),
    };
    const session = { ...base, ...datePatch };
    const serviceOrderCode = session.service_order_code || '';
    const number = resolveCertificateNumber(serviceOrderCode, session.certificate_number, instrument?.lab_id);
    const blob = await generateTemperatureNoSacCertificatePdf(
      { session, instrument, points, results },
      { returnBlob: true, serviceOrderCode },
    );
    return {
      blob,
      number,
      session,
      instrument,
      serviceOrderCode,
      datePatch,
      calibrationDate,
      validUntil: datePatch.valid_until,
    };
  }

  if (row.family === 'flow') {
    const base = await getFlowSession(row.session_id);
    const [points, results] = await Promise.all([
      listFlowPoints(row.session_id),
      listFlowResults(row.session_id),
    ]);
    if (!results?.length) throw new Error('La sesión no tiene resultados calculados.');

    const months = Number(base.validity_months) || DEFAULT_FLOW_VALIDITY_MONTHS;
    const calibrationDate = asIsoDate(base.calibration_date) || asIsoDate(base.report_issue_date) || todayIso();
    const datePatch = {
      calibration_date: calibrationDate,
      report_issue_date: todayIso(),
      validity_months: months,
      valid_until: asIsoDate(base.valid_until) || addFlowValidityDaysToIsoDate(calibrationDate, months),
    };
    const session = { ...base, ...datePatch };
    const serviceOrderCode = session.service_order_code || '';
    const number = resolveCertificateNumber(serviceOrderCode, session.certificate_number, instrument?.lab_id);
    const blob = await generateFlowNoSacCertificatePdf(
      { session, instrument, points, results },
      { returnBlob: true, serviceOrderCode },
    );
    return {
      blob,
      number,
      session,
      instrument,
      serviceOrderCode,
      datePatch,
      calibrationDate,
      validUntil: datePatch.valid_until,
    };
  }

  if (row.family === 'dimensional') {
    const base = await getDimensionalSession(row.session_id);
    const [blocks, points, results] = await Promise.all([
      listDimensionalBlocks(row.session_id),
      listDimensionalPoints(row.session_id),
      listDimensionalResults(row.session_id),
    ]);
    if (!results?.length) throw new Error('La sesión no tiene resultados calculados.');
    const months = Number(base.validity_months) || DEFAULT_DIMENSIONAL_VALIDITY_MONTHS;
    const calibrationDate = asIsoDate(base.calibration_date) || asIsoDate(base.report_issue_date) || todayIso();
    const datePatch = {
      calibration_date: calibrationDate,
      report_issue_date: todayIso(),
      validity_months: months,
      valid_until: asIsoDate(base.valid_until) || addDimensionalValidityDaysToIsoDate(calibrationDate, months),
    };
    const session = { ...base, ...datePatch };
    const serviceOrderCode = session.service_order_code || '';
    const number = resolveCertificateNumber(serviceOrderCode, session.certificate_number, instrument?.lab_id);
    const blob = await generateDimensionalNoSacCertificatePdf(
      { session, instrument, blocks, points, results },
      { returnBlob: true, serviceOrderCode },
    );
    return { blob, number, session, instrument, serviceOrderCode, datePatch, calibrationDate, validUntil: datePatch.valid_until };
  }

  if (row.family === 'environmental') {
    const base = await getEnvironmentalSession(row.session_id);
    const [points, results] = await Promise.all([
      listEnvironmentalPoints(row.session_id),
      listEnvironmentalResults(row.session_id),
    ]);
    if (!results?.length) throw new Error('La sesión no tiene resultados calculados.');
    const months = Number(base.validity_months) || DEFAULT_ENVIRONMENTAL_VALIDITY_MONTHS;
    const calibrationDate = asIsoDate(base.calibration_date) || asIsoDate(base.report_issue_date) || todayIso();
    const datePatch = {
      calibration_date: calibrationDate,
      report_issue_date: todayIso(),
      validity_months: months,
      valid_until: asIsoDate(base.valid_until) || addEnvironmentalValidityDaysToIsoDate(calibrationDate, months),
    };
    const session = { ...base, ...datePatch };
    const serviceOrderCode = session.service_order_code || '';
    const number = resolveCertificateNumber(serviceOrderCode, session.certificate_number, instrument?.lab_id);
    const blob = await generateEnvironmentalNoSacCertificatePdf(
      { session, instrument, points, results },
      { returnBlob: true, serviceOrderCode },
    );
    return { blob, number, session, instrument, serviceOrderCode, datePatch, calibrationDate, validUntil: datePatch.valid_until };
  }

  if (row.family === 'analytical') {
    const base = await getAnalyticalSession(row.session_id);
    const [points, results] = await Promise.all([
      listAnalyticalPoints(row.session_id),
      listAnalyticalResults(row.session_id),
    ]);
    if (!results?.length) throw new Error('La sesión no tiene resultados calculados.');
    const months = Number(base.validity_months) || DEFAULT_ANALYTICAL_VALIDITY_MONTHS;
    const calibrationDate = asIsoDate(base.calibration_date) || asIsoDate(base.report_issue_date) || todayIso();
    const datePatch = {
      calibration_date: calibrationDate,
      report_issue_date: todayIso(),
      validity_months: months,
      valid_until: asIsoDate(base.valid_until) || addAnalyticalValidityDaysToIsoDate(calibrationDate, months),
    };
    const session = { ...base, ...datePatch };
    const serviceOrderCode = session.service_order_code || '';
    const number = resolveCertificateNumber(serviceOrderCode, session.certificate_number, instrument?.lab_id);
    const blob = await generateAnalyticalNoSacCertificatePdf(
      { session, instrument, points, results },
      { returnBlob: true, serviceOrderCode },
    );
    return { blob, number, session, instrument, serviceOrderCode, datePatch, calibrationDate, validUntil: datePatch.valid_until };
  }

  if (row.family === 'mass') {
    const base = await getMassSession(row.session_id);
    const [points, results] = await Promise.all([
      listMassPoints(row.session_id),
      listMassResults(row.session_id),
    ]);
    if (!results?.length) throw new Error('La sesión no tiene resultados calculados.');

    const months = Number(base.validity_months) || DEFAULT_MASS_VALIDITY_MONTHS;
    const calibrationDate = asIsoDate(base.calibration_date) || asIsoDate(base.report_issue_date) || todayIso();
    const datePatch = {
      calibration_date: calibrationDate,
      report_issue_date: todayIso(),
      validity_months: months,
      valid_until: asIsoDate(base.valid_until) || addMassValidityDaysToIsoDate(calibrationDate, months),
    };
    const session = { ...base, ...datePatch };
    const serviceOrderCode = session.service_order_code || '';
    const number = resolveCertificateNumber(serviceOrderCode, session.certificate_number, instrument?.lab_id);
    const blob = await generateMassNoSacCertificatePdf(
      { session, instrument, points, results },
      { returnBlob: true, serviceOrderCode },
    );
    return {
      blob, number, session, instrument, serviceOrderCode, datePatch,
      calibrationDate, validUntil: datePatch.valid_until,
    };
  }

  if (row.family === 'force') {
    const base = await getForceSession(row.session_id);
    const results = await listForceResults(row.session_id);
    if (!results?.length) throw new Error('La sesión no tiene resultados calculados.');

    const months = Number(base.validity_months) || DEFAULT_FORCE_VALIDITY_MONTHS;
    const calibrationDate = asIsoDate(base.calibration_date) || asIsoDate(base.report_issue_date) || todayIso();
    const datePatch = {
      calibration_date: calibrationDate,
      report_issue_date: todayIso(),
      validity_months: months,
      valid_until: asIsoDate(base.valid_until) || addForceValidityDaysToIsoDate(calibrationDate, months),
    };
    const session = { ...base, ...datePatch };
    const serviceOrderCode = session.service_order_code || '';
    const number = resolveCertificateNumber(serviceOrderCode, session.certificate_number, instrument?.lab_id);
    const blob = await generateForceNoSacCertificatePdf(
      { session, instrument, results },
      { returnBlob: true, serviceOrderCode },
    );
    return {
      blob, number, session, instrument, serviceOrderCode, datePatch,
      calibrationDate, validUntil: datePatch.valid_until,
    };
  }

  if (row.family === 'gas') {
    const base = await getGasSession(row.session_id);
    const results = await listGasResults(row.session_id);
    if (!results?.length) throw new Error('La sesión no tiene resultados calculados.');
    const months = Number(base.validity_months) || DEFAULT_GAS_VALIDITY_MONTHS;
    const calibrationDate = asIsoDate(base.calibration_date) || asIsoDate(base.report_issue_date) || todayIso();
    const datePatch = {
      calibration_date: calibrationDate,
      report_issue_date: todayIso(),
      validity_months: months,
      valid_until: asIsoDate(base.valid_until) || addGasValidityDaysToIsoDate(calibrationDate, months),
    };
    const session = { ...base, ...datePatch };
    const serviceOrderCode = session.service_order_code || '';
    const number = resolveCertificateNumber(serviceOrderCode, session.certificate_number, instrument?.lab_id);
    const blob = await generateGasNoSacCertificatePdf(
      { session, instrument, results },
      { returnBlob: true, serviceOrderCode },
    );
    return { blob, number, session, instrument, serviceOrderCode, datePatch, calibrationDate, validUntil: datePatch.valid_until };
  }

  if (row.family === 'valve') {
    const { data: valveHeader, error: valveHeaderError } = await supabase
      .from('calibration_sessions').select('template_code').eq('id', row.session_id).eq('family', 'valve').single();
    if (valveHeaderError) throw valveHeaderError;
    const isCheckValve = valveHeader?.template_code === 'VALVE:check_nonreturn:v1';
    const base = isCheckValve ? await getCheckValveSession(row.session_id) : await getSafetyValveSession(row.session_id);
    const results = isCheckValve ? await listCheckValveResults(row.session_id) : await listSafetyValveResults(row.session_id);
    if (!results?.length) throw new Error('La sesión no tiene resultados calculados.');
    const months = Number(base.validity_months) || DEFAULT_VALVE_VALIDITY_MONTHS;
    const calibrationDate = asIsoDate(base.calibration_date) || asIsoDate(base.report_issue_date) || todayIso();
    const datePatch = {
      calibration_date: calibrationDate,
      report_issue_date: todayIso(),
      validity_months: months,
      valid_until: asIsoDate(base.valid_until) || addValveValidityDaysToIsoDate(calibrationDate, months),
    };
    const session = { ...base, ...datePatch };
    const serviceOrderCode = session.service_order_code || '';
    const number = resolveCertificateNumber(serviceOrderCode, session.certificate_number, instrument?.lab_id);
    const blob = await (isCheckValve ? generateCheckValveNoSacCertificatePdf : generateSafetyValveNoSacCertificatePdf)(
      { session, instrument, results },
      { returnBlob: true, serviceOrderCode },
    );
    return { blob, number, session, instrument, serviceOrderCode, datePatch, calibrationDate, validUntil: datePatch.valid_until };
  }

  if (row.family === 'pressure') {
    const base = await getPressureManometerSession(row.session_id);
    const results = await listPressureManometerResults(row.session_id);
    if (!results?.length) throw new Error('La sesión no tiene resultados calculados.');

    const months = Number(base.validity_months) || PRESSURE_MONTHS;
    const calibrationDate = asIsoDate(base.calibration_date) || asIsoDate(base.report_issue_date) || todayIso();
    const datePatch = {
      calibration_date: calibrationDate,
      report_issue_date: todayIso(),
      validity_months: months,
      expiration_date: asIsoDate(base.expiration_date) || addPressureValidityDaysToIsoDate(calibrationDate, months),
    };
    const session = { ...base, ...datePatch };

    const number = resolveCertificateNumber(session.certificate_number, instrument?.lab_id);
    const blob = await generatePressureNoSacCertificatePdf({ session, instrument, results }, { returnBlob: true });
    return {
      blob, number, session, instrument, serviceOrderCode: null, datePatch,
      calibrationDate, validUntil: datePatch.expiration_date,
    };
  }

  throw new Error(`Familia sin generador de certificado: ${row.family}`);
}

// Vista previa para que el jefe mire el PDF ANTES de aprobar (no persiste nada).
export async function previewCertificate(row) {
  if (row.family === 'external') {
    // Externo: ya está subido, se muestra tal cual (no se genera nada).
    const { data } = await supabase
      .from('certificates')
      .select('pdf_url, certificate_number')
      .eq('id', row.session_id)
      .single();
    return { blob: null, number: data?.certificate_number, url: data?.pdf_url };
  }
  const { blob, number } = await buildCertificate(row);
  return { blob, number, url: URL.createObjectURL(blob) };
}

// ── Subir un certificado EXTERNO (instrumentos especiales) ──────────────────
// El técnico arma el PDF por fuera y lo sube. Queda 'pending' → el jefe aprueba.
export async function uploadExternalCertificate({
  instrument,
  file,
  certificateNumber,
  calibrationDate,
  validUntil,
  serviceOrderCode = '',
}) {
  if (!file) throw new Error('Elegí el archivo PDF del certificado.');
  if (file.type !== 'application/pdf') throw new Error('El certificado tiene que ser un PDF.');
  const number = String(certificateNumber || '').trim();
  if (!number) throw new Error('Ingresá el número de certificado.');
  if (!instrument?.id) throw new Error('Instrumento inválido.');

  // Completar lab_id/client_id si el que llama pasó un instrumento parcial
  // (la fila del panel del técnico no trae esos campos).
  let inst = instrument;
  if (inst.client_id == null || inst.lab_id == null) {
    const { data } = await supabase
      .from('instruments')
      .select('id, lab_id, client_id, tag')
      .eq('id', instrument.id)
      .single();
    if (data) inst = { ...instrument, ...data };
  }
  const clientId = inst.client_id || null;
  const labId = inst.lab_id || inst.id;
  const certificateId = crypto.randomUUID();
  const storagePath = certificateObjectPath(clientId, labId, certificateId, true);
  const artifactSha256 = await sha256(file);

  // 1) Subir el PDF (la política del bucket ya permite subida de staff autenticado).
  const { error: upErr } = await supabase.storage
    .from(BUCKET)
    .upload(storagePath, file, { upsert: false, contentType: 'application/pdf' });
  if (upErr) throw upErr;
  const { data: pub } = supabase.storage.from(BUCKET).getPublicUrl(storagePath);
  const pdfUrl = pub?.publicUrl || null;

  // 2) Registrar como PENDIENTE externo (el jefe lo aprueba después).
  const { data: cert, error } = await supabase
    .from('certificates')
    .insert({
      certificate_number: number,
      id: certificateId,
      artifact_sha256: artifactSha256,
      artifact_bytes: file.size,
      instrument_id: instrument.id,
      client_id: clientId,
      family: 'external',
      source_table: 'external',
      source_session_id: null,
      certificate_type: 'NO_SAC',
      service_order_code: serviceOrderCode || null,
      calibration_date: asIsoDate(calibrationDate) || null,
      valid_until: asIsoDate(validUntil) || null,
      storage_path: storagePath,
      pdf_url: pdfUrl,
      status: 'pending',
      is_external: true,
      uploaded_by: await currentUserId(),
      uploaded_at: new Date().toISOString(),
    })
    .select('*')
    .single();
  if (error) throw error;
  return cert;
}

// La revisión es un hito independiente. Si el instrumento ya fue remitido o
// entregado, aprobar el certificado no debe hacerlo retroceder a "reviewed".
// La migración de base de datos aplica la misma regla como última barrera; esta
// lectura mantiene el comportamiento correcto aun durante un despliegue gradual.
async function markInstrumentReviewed(instrumentId, { calibrationDate, validUntil } = {}) {
  const reviewerId = await currentUserId();
  const reviewedAt = new Date().toISOString();
  const { data: current, error: readError } = await supabase
    .from('instruments')
    .select('status')
    .eq('id', instrumentId)
    .single();
  if (readError) throw readError;

  const instPatch = {
    status: isClosedInstrumentStatus(current?.status) ? current.status : 'reviewed',
    reviewed_at: reviewedAt,
    reviewed_by: reviewerId,
  };
  if (calibrationDate) instPatch.lastcalibrationdate = calibrationDate;
  if (validUntil) instPatch.nextcalibrationdate = validUntil;

  const { error } = await supabase
    .from('instruments')
    .update(instPatch)
    .eq('id', instrumentId);
  if (error) throw error;
}

// ── Aprobar un certificado EXTERNO (ya subido): solo se aprueba ─────────────
async function approveExternalCertificate(row) {
  const { data: cert, error } = await supabase
    .from('certificates')
    .update({ status: 'approved', approved_by: await currentUserId(), approved_at: new Date().toISOString() })
    .eq('id', row.session_id)
    .eq('status', 'pending')
    .select('*')
    .single();
  if (error) throw error;

  // Registrar la revisión sin retroceder un instrumento ya remitido/entregado
  // + actualizar su ventana de validez (no fatal: lo importante es el cert).
  try {
    await markInstrumentReviewed(row.instrument_id, {
      calibrationDate: cert.calibration_date,
      validUntil: cert.valid_until,
    });
  } catch (e) {
    console.warn('[approveExternal] instrumento no actualizado', e);
  }
  return cert;
}

// ── Aprobar: emite, sube y registra ─────────────────────────────────────────
export async function approveCertificate(row) {
  if (row.family === 'external') return approveExternalCertificate(row);
  const built = await buildCertificate(row);
  const { blob, number, session, instrument, serviceOrderCode, calibrationDate, validUntil, datePatch } = built;
  if (!number) throw new Error('No se pudo determinar el número de certificado.');

  const clientId = instrument?.client_id || null;
  const certificateId = crypto.randomUUID();
  const storagePath = certificateObjectPath(clientId, instrument?.lab_id || instrument?.id, certificateId);
  const artifactSha256 = await sha256(blob);

  // 1) PDF a la carpeta del cliente
  const { error: upErr } = await supabase.storage
    .from(BUCKET)
    .upload(storagePath, blob, { upsert: false, contentType: 'application/pdf' });
  if (upErr) throw upErr;
  const { data: pub } = supabase.storage.from(BUCKET).getPublicUrl(storagePath);
  const pdfUrl = pub?.publicUrl || null;

  // 2) Registro único (lo usan el link corto, el buscador y el portal)
  const { data: cert, error: cErr } = await supabase
    .from('certificates')
    .insert({
      certificate_number: number,
      id: certificateId,
      artifact_sha256: artifactSha256,
      artifact_bytes: blob.size,
      instrument_id: row.instrument_id,
      client_id: clientId,
      family: row.family,
      source_table: row.source_table,
      source_session_id: row.session_id,
      certificate_type: session?.certificate_type || 'NO_SAC',
      service_order_code: serviceOrderCode,
      calibration_date: calibrationDate || null,
      valid_until: validUntil || null,
      storage_path: storagePath,
      pdf_url: pdfUrl,
      approved_by: await currentUserId(),
    })
    .select('*')
    .single();
  if (cErr) throw cErr;

  // 3) Cerrar la sesión con sus fechas/número/URL definitivos
  const patch = {
    ...datePatch,
    certificate_number: number,
    certificate_url: pdfUrl,
    certificate_created_at: new Date().toISOString(),
    status: 'closed',
  };
  if (row.family === 'electrical') await updateElectricalSession(row.session_id, patch);
  else if (row.family === 'temperature') await updateTemperatureSession(row.session_id, patch);
  else if (row.family === 'flow') await updateFlowSession(row.session_id, patch);
  else if (row.family === 'dimensional') await updateDimensionalSession(row.session_id, patch);
  else if (row.family === 'environmental') await updateEnvironmentalSession(row.session_id, patch);
  else if (row.family === 'analytical') await updateAnalyticalSession(row.session_id, patch);
  else if (row.family === 'mass') await updateMassSession(row.session_id, patch);
  else if (row.family === 'force') await updateForceSession(row.session_id, patch);
  else if (row.family === 'gas') await updateGasSession(row.session_id, patch);
  else if (row.family === 'valve') {
    const { data: valveHeader } = await supabase.from('calibration_sessions').select('template_code').eq('id', row.session_id).single();
    if (valveHeader?.template_code === 'VALVE:check_nonreturn:v1') await updateCheckValveSession(row.session_id, patch);
    else await updateSafetyValveSession(row.session_id, patch);
  }
  else await updatePressureManometerSession(row.session_id, patch);

  // 4) Registrar la revisión sin retroceder un instrumento ya
  // remitido/entregado + actualizar su ventana de validez.
  await markInstrumentReviewed(row.instrument_id, { calibrationDate, validUntil });

  return cert;
}

// ── Rechazar: vuelve al técnico con el motivo ───────────────────────────────
export async function rejectCertificate(row, reason) {
  const motivo = String(reason || '').trim();
  if (!motivo) throw new Error('El motivo del rechazo es obligatorio.');

  if (row.family === 'external') {
    // Externo: se anula el pendiente con el motivo (no hay sesión a la que volver).
    const { error } = await supabase
      .from('certificates')
      .update({ status: 'void', review_notes: motivo })
      .eq('id', row.session_id)
      .eq('status', 'pending');
    if (error) throw error;
    return;
  }

  const patch = { status: 'in_progress', review_notes: motivo };
  if (row.family === 'electrical') await updateElectricalSession(row.session_id, patch);
  else if (row.family === 'temperature') await updateTemperatureSession(row.session_id, patch);
  else if (row.family === 'flow') await updateFlowSession(row.session_id, patch);
  else if (row.family === 'dimensional') await updateDimensionalSession(row.session_id, patch);
  else if (row.family === 'environmental') await updateEnvironmentalSession(row.session_id, patch);
  else if (row.family === 'analytical') await updateAnalyticalSession(row.session_id, patch);
  else if (row.family === 'mass') await updateMassSession(row.session_id, patch);
  else if (row.family === 'force') await updateForceSession(row.session_id, patch);
  else if (row.family === 'gas') await updateGasSession(row.session_id, patch);
  else if (row.family === 'valve') {
    const { data: valveHeader } = await supabase.from('calibration_sessions').select('template_code').eq('id', row.session_id).single();
    if (valveHeader?.template_code === 'VALVE:check_nonreturn:v1') await updateCheckValveSession(row.session_id, patch);
    else await updateSafetyValveSession(row.session_id, patch);
  }
  else await updatePressureManometerSession(row.session_id, patch);

  // Vuelve a manos del técnico (NO se usa "rejected": ese estado significa que
  // el INSTRUMENTO fue rechazado, no que haya que rehacer la medición).
  const { error } = await supabase.from('instruments').update({ status: 'in_progress' }).eq('id', row.instrument_id);
  if (error) throw error;
}

// ── Consultas ───────────────────────────────────────────────────────────────
export async function listCertificatesByInstrument(instrumentId) {
  const { data, error } = await supabase
    .from('certificates')
    .select('*')
    .eq('instrument_id', instrumentId)
    .eq('status', 'approved')
    .order('approved_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

// Buscador para jefe y administrativos: una fila por instrumento con su
// certificado vigente. El texto libre busca por serie, TAG, ID, nº de
// certificado, marca, modelo y cliente.
export async function searchCertificates({
  q = '',
  type = '',
  vigencia = '',
  from = '',
  to = '',
  limit = 200,
} = {}) {
  let query = supabase.from('v_certificate_search').select('*');

  // PostgREST usa la coma y los paréntesis como separadores del `or`, así que
  // hay que sacarlos del término o la consulta sale mal formada.
  const term = String(q || '').replace(/[,()]/g, ' ').trim();
  if (term) {
    const like = `*${term}*`;
    query = query.or(
      [
        `serial.ilike.${like}`,
        `tag.ilike.${like}`,
        `lab_id_text.ilike.${like}`,
        `certificate_number.ilike.${like}`,
        `brand.ilike.${like}`,
        `model.ilike.${like}`,
        `client_name.ilike.${like}`,
      ].join(',')
    );
  }
  if (type) query = query.eq('type', type);
  if (vigencia) query = query.eq('vigencia', vigencia);
  if (from) query = query.gte('calibration_date', from);
  if (to) query = query.lte('calibration_date', to);

  const { data, error } = await query.order('lab_id', { ascending: false }).limit(limit);
  if (error) throw error;
  return data || [];
}

export async function getCertificateByNumber(number) {
  const { data, error } = await supabase
    .from('certificates')
    .select('*')
    .eq('certificate_number', String(number))
    .eq('status', 'approved')
    .maybeSingle();
  if (error) throw error;
  return data || null;
}
